import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  const loanTypes = [
    { name: 'Personal Loan', path: '/personal-loan' },
    { name: 'Home Loan', path: '/home-loan' },
    { name: 'Loan Against Property', path: '/lap' },
    { name: 'Gold Loan', path: '/gold-loan' }
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact Us', path: '/contact' },
    { name: 'Financial Insights', path: '/blog' },
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms & Conditions', path: '/terms-conditions' },
    { name: 'Lending Partners', path: '/lending-partners' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_loan-emi-calc/artifacts/zawsqeww_white%20-%202897_Logo_V-PNG-08.png" 
                alt="PFIL Finance Logo" 
                className="h-14 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold">PFIL Finance</span>
                <span className="text-sm text-gray-400">Since 1996</span>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Pooja Finstock International Limited (PFIL) is an RBI-registered NBFC providing 
              innovative financial solutions for over 29 years with complete compliance and transparency.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61578619573930" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white hover:scale-110 transition-all duration-300"
                aria-label="Follow PFIL Finance on Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://x.com/pfilfinance" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white hover:scale-110 transition-all duration-300"
                aria-label="Follow PFIL Finance on X (Twitter)"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/pfilfinance/" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white hover:scale-110 transition-all duration-300"
                aria-label="Follow PFIL Finance on Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/pfilfinance/" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white hover:scale-110 transition-all duration-300"
                aria-label="Connect with PFIL Finance on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Loan Products */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Our Loan Products</h3>
            <ul className="space-y-2">
              {loanTypes.map((loan, index) => (
                <li key={index}>
                  <Link 
                    to={loan.path} 
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {loan.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <a 
                  href="tel:+91022 69718828" 
                  className="text-sm text-gray-300 hover:text-white transition-colors hover:underline"
                >
                  022 69718828
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <a 
                  href="mailto:support@pfilfinance.com" 
                  className="text-sm text-gray-300 hover:text-white transition-colors hover:underline"
                >
                  support@pfilfinance.com
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                <div className="text-sm text-gray-300">
                  <p><strong>Registered Office:</strong></p>
                  <p>909, SUKHSAGAR COMPLEX,<br />
                  NR. FORTUNE LANDMARK HOTEL,<br />
                  USMANPURA, AHMEDABAD,<br />
                  Gujarat, India - 380013</p>
                  
                  <p className="mt-4"><strong>Corporate Office:</strong></p>
                  <p>Office-B 6010, H-core Solitaire Business Hub,<br />
                  Viman Nagar,<br />
                  Pune, Maharashtra 411014</p>
                  
                  <p className="mt-4"><strong>Branch Office:</strong></p>
                  <p>Ground Floor, Sai Anand Pride B,<br />
                  Duber Naka, Sinnar,<br />
                  Nashik - 422103</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400">
              <p>&copy; 2025 Pooja Finstock International Limited. All rights reserved.</p>
              <p className="mt-1">RBI Registration No: B.01.00449 | CIN: U67120GJ1996PLC028614</p>
            </div>
            <div className="text-sm text-gray-400">
              <p>Designed & Developed with ❤️</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;